import React from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tab,
  Tabs,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { LivestreamProgramPeriodDayDetails } from "./LivestreamProgramPeriodDayDialog";
import type { LivestreamProgramPeriodDayDetailsProps } from "./LivestreamProgramPeriodDayDialog";

export type PeriodChartKind = "views" | "interactions" | "live";

export type LivestreamProgramPeriodChartDialogProps = {
  open: boolean;
  chart: PeriodChartKind | null;
  onChartChange: (chart: PeriodChartKind) => void;
  chartLabels: Record<PeriodChartKind, string>;
  availableCharts: PeriodChartKind[];
  onClose: () => void;
  children: React.ReactNode;
} & Omit<LivestreamProgramPeriodDayDetailsProps, "date"> & {
  selectedDate: string | null;
};

export const LivestreamProgramPeriodChartDialog: React.FC<
  LivestreamProgramPeriodChartDialogProps
> = ({
  open,
  chart,
  onChartChange,
  chartLabels,
  availableCharts,
  onClose,
  children,
  selectedDate,
  views,
  interactions,
  live,
  livestreams,
  showProgramName,
  competing,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: "min(1680px, 96vw)",
          height: "min(880px, 92vh)",
          maxHeight: "92vh",
          m: 2,
        },
      }}
    >
      <DialogTitle sx={{ p: 0, pr: 1.5, borderBottom: 1, borderColor: "divider" }}>
        <Stack direction="row" alignItems="center">
          <Tabs
            value={chart ?? false}
            onChange={(_, next: PeriodChartKind) => onChartChange(next)}
            aria-label={chartLabels.views}
            sx={{ flex: 1, minHeight: 48, px: 1.5 }}
          >
            {availableCharts.map((kind) => (
              <Tab key={kind} value={kind} label={chartLabels[kind]} />
            ))}
          </Tabs>
          <IconButton
            size="small"
            onMouseDown={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onClose();
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent
        sx={{
          p: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          height: "100%",
        }}
      >
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "grid",
            gridTemplateRows: "minmax(280px, 1.15fr) minmax(240px, 1fr)",
          }}
        >
          <Box
            sx={{
              minWidth: 0,
              minHeight: 0,
              overflow: "auto",
              p: 2.5,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box sx={{ flex: 1, minHeight: 240 }}>{children}</Box>
          </Box>
          <Box
            sx={{
              minWidth: 0,
              minHeight: 0,
              overflow: "auto",
              borderTop: "1px solid",
              borderColor: "divider",
              p: 2.5,
              bgcolor: "background.default",
            }}
          >
            <LivestreamProgramPeriodDayDetails
              date={selectedDate}
              views={views}
              interactions={interactions}
              live={live}
              livestreams={livestreams}
              showProgramName={showProgramName}
              competing={competing}
            />
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
